import { type FC, type ReactNode } from "react";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId, SurfaceType } from "../../gql-generated/types";
import { Flexbox, LabelWithTooltip } from "../../shared/components";
import { SpectaclesTags } from "../../spectacles-tags/components";
import { useTagWithValidation } from "../hooks";

import { TagsInputContainer } from "./Tags.styles";
import { TagsContainer } from "./TagsContainer";
import { TagsErrorMessage } from "./TagsErrorMessage";
import { TagsInput } from "./TagsInput";
import { TrendingTagAccordion } from "./TrendingTagAccordion";

interface TagsProps {
    addTag: (name: string) => void;
    removeTag: (name: string) => void;
    tags: readonly string[];
    placeholder: string;
    label?: string;
    tooltipContent?: ReactNode;
    lensSurfaceType?: SurfaceType;
}

export const Tags: FC<TagsProps> = ({
    addTag,
    removeTag,
    tags,
    placeholder,
    label,
    tooltipContent,
    lensSurfaceType = SurfaceType.SurfaceTypeSnapchat,
}) => {
    const {
        tagsValidation,
        onInputTagChange,
        addTagFromInputField,
        addTagPreValidated,
        removeTagWithValidation,
        inputRef,
    } = useTagWithValidation(tags, addTag, removeTag);
    const { configs } = useFeatureGates();

    const isTrendingTagsEnabled = configs[CofConfigId.MyLensesTrendingTags].enabled;
    const isSpectaclesLens = lensSurfaceType === SurfaceType.SurfaceTypeSpectacles;

    return (
        <>
            {label ? <LabelWithTooltip label={label} content={tooltipContent} /> : null}
            <Flexbox flexDirection="column" alignItems="flex-start">
                {!isSpectaclesLens && (
                    <TagsInputContainer>
                        <TagsInput
                            onAddTag={addTagFromInputField}
                            onInputTagChange={onInputTagChange}
                            inputRef={inputRef}
                            placeholder={placeholder}
                        />
                        <TagsErrorMessage tagsValidationResult={tagsValidation} />
                    </TagsInputContainer>
                )}
                <TagsContainer tagsList={tags} onRemoveTag={removeTagWithValidation} />
                {isTrendingTagsEnabled && lensSurfaceType === SurfaceType.SurfaceTypeSnapchat && (
                    <TrendingTagAccordion onRowClick={addTagPreValidated} />
                )}
                {isSpectaclesLens && <SpectaclesTags onTagClick={addTagPreValidated} selectedTags={tags} />}
            </Flexbox>
        </>
    );
};
